import React, {
  ElementType,
} from 'react';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import BloodtypeRoundedIcon from '@mui/icons-material/BloodtypeRounded';
import DeviceThermostatRoundedIcon from '@mui/icons-material/DeviceThermostatRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import isNil from 'lodash/isNil';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatVitalNumber,
} from 'core/vitals/utils';
import {
  TrendChartPoint,
  VitalMetric,
  VitalMetricId,
} from 'core/vitals/types';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

type MetricHeaderProps = {
  metric: VitalMetric;
  showUnit?: boolean;
  showSeries?: boolean;
};

type TooltipPayload = {
  dataKey?: string | number;
  value?: number;
  payload?: TrendChartPoint;
};

type TooltipProps = {
  active?: boolean;
  payload?: TooltipPayload[];
  metric: VitalMetric;
};

type TrendChartProps = {
  metric: VitalMetric;
  points: TrendChartPoint[];
  axes?: boolean;
  compact?: boolean;
  fillArea?: boolean;
  emptyText: string;
};

const metricIcons: Record<VitalMetricId, ElementType> = {
  'blood-pressure': BloodtypeRoundedIcon,
  'heart-rate': FavoriteBorderRoundedIcon,
  'respiratory-rate': AirRoundedIcon,
  temperature: DeviceThermostatRoundedIcon,
  'oxygen-saturation': WaterDropRoundedIcon,
};

export function VitalMetricHeader({
  metric,
  showUnit = true,
  showSeries = true,
}: MetricHeaderProps) {
  const Icon = metricIcons[metric.id];
  const iconColor = metric.id === 'blood-pressure' ? '#b91c1c' : metric.series[0].color;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className="!h-4 !w-4 shrink-0" style={{ color: iconColor }} />
      <span className="min-w-0 text-[11px] font-semibold uppercase leading-tight tracking-[0.14em] text-gray-500">
        {metric.title}
      </span>
      {showUnit && <span className="text-[11px] font-medium text-gray-400">{metric.unit}</span>}
      {showSeries && metric.series.length > 1 && metric.series.map(({ key, color, label }) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-500"
        >
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function TrendTooltip({ active, payload, metric }: TooltipProps) {
  if (!active || isNil(payload) || payload.length === 0) {
    return null;
  }

  const point = payload[0]?.payload;
  const readings = metric.series.map(({ key, color }) => {
    const value = payload.find((entry) => entry.dataKey === key)?.value;

    return {
      key,
      color,
      value: typeof value === 'number' ? formatVitalNumber(value, key) : DEFAULT_EMPTY_VALUE,
    };
  });
  const isBloodPressure = metric.id === 'blood-pressure';
  const bloodPressureReading = readings.map(({ value }) => value).join(' / ');

  return (
    <div className="rounded-md border border-gray-200/90 bg-white/95 px-3 py-2 shadow-[0_12px_28px_-16px_rgba(15,23,42,0.45)] backdrop-blur-sm">
      <div className="mb-1 text-[11px] font-medium text-gray-500">{point?.fullLabel}</div>
      <div className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-950">
        {isBloodPressure ? (
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#b91c1c]" />
            {bloodPressureReading}
          </span>
        ) : readings.map((reading) => (
          <span key={reading.key} className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: reading.color }} />
            {reading.value}
          </span>
        ))}
        <span className="ml-1 text-[11px] font-medium text-gray-500">{metric.unit}</span>
      </div>
      {point?.encounterDetails && (
        <div className="mt-1 max-w-52 text-xs leading-4 text-gray-500">
          {point.encounterDetails}
        </div>
      )}
    </div>
  );
}

export function VitalTrendChart({
  metric,
  points,
  axes = false,
  compact = false,
  fillArea = true,
  emptyText,
}: TrendChartProps) {
  const gradientId = `vitals-trend-${metric.id}`;
  const primaryKey = metric.series[0].key;
  const primaryColor = metric.series[0].color;

  return (
    points.length === 0
      ? (
      <div className="flex h-full items-center justify-center rounded-md border border-dashed border-gray-200 bg-white text-sm text-gray-500">
        {emptyText}
      </div>
      )
      : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={points}
            margin={{
              top: axes ? 12 : 6,
              right: axes ? 16 : 8,
              bottom: axes ? 4 : 0,
              left: axes ? 4 : 8,
            }}
          >
            {fillArea && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={`${primaryColor}cc`} />
                  <stop offset="100%" stopColor={`${primaryColor}08`} />
                </linearGradient>
              </defs>
            )}
            {axes && <CartesianGrid vertical={false} stroke="#e5e7eb" />}
            <Tooltip
              cursor={axes ? { stroke: '#cbd5e1', strokeWidth: 1 } : false}
              content={<TrendTooltip metric={metric} />}
            />
            {axes && (
              <>
                <XAxis
                  dataKey="fullLabel"
                  axisLine={false}
                  tickLine={false}
                  minTickGap={56}
                  interval="preserveStartEnd"
                  padding={{ left: 8, right: 8 }}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  tickMargin={8}
                  tickFormatter={(value) => String(value).split(' ')[0]}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={34}
                  tick={{ fill: '#6b7280', fontSize: 11, dy: -4 }}
                  tickFormatter={(value) => formatVitalNumber(Number(value))}
                />
              </>
            )}
            <Area
              type="linear"
              dataKey={primaryKey}
              stroke={primaryColor}
              strokeWidth={2.5}
              fill={fillArea ? `url(#${gradientId})` : 'none'}
              fillOpacity={1}
              dot={{
                r: compact ? 2.5 : 2.5,
                fill: primaryColor,
                stroke: primaryColor,
                strokeWidth: 1.5,
              }}
              activeDot={{
                r: 4,
                fill: primaryColor,
                stroke: primaryColor,
                strokeWidth: 2,
              }}
            />
            {metric.series.slice(1).map(({ key, color }) => (
              <Line
                key={key}
                type="linear"
                dataKey={key}
                stroke={color}
                strokeWidth={2.25}
                dot={{ r: 2.5, fill: color, stroke: color, strokeWidth: 1.5 }}
                activeDot={{ r: 4, fill: color, stroke: color, strokeWidth: 2 }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )
  );
}
